import { Component, HostListener, OnDestroy, OnInit, ViewChild } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { ProductCategories, ProductAttributes, DiscountRange, ProductQueryCategory } from "@models/product.model";
import { ProductService } from "../../services/product/product.service";
import { environment } from "~/src/environments/environment";
import { appConfig } from "~/src/environments/app.config";
import { MatSelectChange } from "@angular/material/select";
import { CartItemService } from "../../services/cart-item/cart-item.service";
import { LocalStorageService } from "../../services/local-storage/local-storage.service";
import { AuthService } from "../../services/auth/auth.service";
import { BaseCart, CartAttributes } from "@models/cart.model";
import { debounceTime, distinctUntilChanged, Subject, Subscription } from "rxjs";
import { TranslateService } from "@ngx-translate/core";
import { RightNavbarComponent } from "../../dashboard/right-navbar/right-navbar.component";
import { DialogService } from "../../services/dialog/dialog.service";
import { Location } from "@angular/common";

@Component({
    selector: "product-viewlist",
    templateUrl: "./product-viewlist.component.html",
    styleUrls: ["./product-viewlist.component.scss"],
    standalone: false,
})
export class ProductViewlistComponent implements OnInit, OnDestroy {
    public category!: string;
    public isLoading = false;
    public productList: ProductAttributes[] = [];
    public cartItems: CartAttributes[] = [];
    public message: string = "";
    public totalProducts = 0;
    public subCategories = appConfig.commonSubcategories;
    public ratingOptions = [5, 4, 3, 2, 1];
    public discountRanges!: DiscountRange[];
    public selectedSubCategories: string[] = ["All Products"];
    public selectedDiscount!: number | null;
    public seletedReviews: number[] = [];
    public selectedSort: string = "";
    public pageIndex = 0;
    public pageSize = 3;
    public initialPageSize = 9;
    public searchQuery: string = "";
    public wishListIDs: { productId: number }[] = [];
    public productCategories = ProductCategories;
    public availableSubCategories: (string | undefined)[] = [];
    public isNavbarOpen: boolean = false;
    public activeSubCategory: string = "All Products";
    public isListView: boolean = false;
    public isOpenFilter: boolean = true;
    public minPrice: number = 1;
    public maxPrice: number = 10000;
    @ViewChild(RightNavbarComponent) rightNavbarComponent!: RightNavbarComponent;
    private priceChange$ = new Subject<{ min: number; max: number }>();
    private priceChangeSub!: Subscription;

    constructor(
        private route: ActivatedRoute,
        private productService: ProductService,
        private router: Router,
        private cartService: CartItemService,
        private localStorageService: LocalStorageService,
        public authService: AuthService,
        private translate: TranslateService,
        private dialogService: DialogService,
        private location: Location,
    ) {}

    ngOnInit(): void {
        this.route.queryParams.subscribe((params) => {
            const category = params["category"];
            const rawQuery = params["rawQuery"];
            this.searchQuery = rawQuery;
            if (this.searchQuery) {
                this.pageIndex = 0;
            }
            this.productList = [];
            if (category === ProductQueryCategory.Featured) {
                this.category = ProductCategories.Featured;
            } else if (category === ProductQueryCategory.BestSeller) {
                this.category = ProductCategories.BestSeller;
            } else if (category === ProductQueryCategory.Sale) {
                this.category = ProductCategories.OnSale;
            } else if (category === ProductQueryCategory.Block) {
                this.category = ProductCategories.BuyBlocks;
            }
            if (this.category === this.productCategories.BuyBlocks) {
                this.selectedSubCategories = [];
            }
            this.getAvailableSubCategories();
            this.loadProducts(this.initialPageSize);
            // Handles price range updates with debounce and prevents duplicate API calls.
            this.priceChangeSub = this.priceChange$
                .pipe(
                    debounceTime(500),
                    distinctUntilChanged((prev, curr) => prev.min === curr.min && prev.max === curr.max),
                )
                .subscribe(() => {
                    this.productList = [];
                    this.pageIndex = 0;
                    this.loadProducts(this.initialPageSize);
                });
        });

        this.getDiscountRanges();
        this.checkScreenSize();
    }

    public applyPriceFilter(): void {
        // min price validation
        if (isNaN(this.minPrice) || this.minPrice < 1) {
            this.minPrice = 1;
        }
        // max price validation
        if (isNaN(this.maxPrice) || this.maxPrice < this.minPrice) {
            this.maxPrice = this.minPrice;
        }
        // reset pagination
        this.pageIndex = 0;

        // trigger API (debounced)
        this.priceChange$.next({
            min: this.minPrice,
            max: this.maxPrice,
        });
        this.isOpenFilter = false;
    }

    @HostListener("window: resize")
    onResize() {
        this.checkScreenSize();
    }

    public checkScreenSize(): void {
        if (window.innerWidth <= 993) {
            this.isOpenFilter = false;
        } else {
            this.isOpenFilter = true;
        }
    }

    public loadProducts(pageSize: number = environment.dynamicPagination.pageSize): void {
        this.isLoading = true;
        this.getAvailableSubCategories();
        this.productService
            .getAllActiveProducts(
                this.pageIndex,
                pageSize,
                this.searchQuery,
                this.category,
                this.selectedSubCategories,
                this.minPrice,
                this.maxPrice,
                this.selectedSort,
            )
            .subscribe({
                next: (response) => {
                    this.isLoading = false;
                    this.productList = [...this.productList, ...response.rows];
                    this.totalProducts = response.count;
                },
                error: () => {
                    this.isLoading = false;
                },
            });
    }

    public getDiscountRanges(): void {
        this.productService.getDiscountRanges().subscribe({
            next: (res) => {
                this.discountRanges = res;
            },
            error: () => {
                throw new Error();
            },
        });
    }

    public get sortLabel(): string {
        if (this.selectedSort === "hightolow") {
            return this.getTranslation("productList.label.sortPriceHighToLow");
        } else if (this.selectedSort === "lowtohigh") {
            return this.getTranslation("productList.label.sortPriceLowToHigh");
        } else if (this.selectedSort === "highestRated") {
            return "Highest Rated";
        }
        return this.getTranslation("productList.label.sortByPrice");
    }

    onCategoryChange(event: any, filterItem: string): void {
        if (event.checked) {
            // Add if not already in the array
            if (!this.selectedSubCategories.includes(filterItem)) {
                this.selectedSubCategories.push(filterItem);
            }
        } else {
            // Remove it
            this.selectedSubCategories = this.selectedSubCategories.filter((item) => item !== filterItem);
        }
        this.productList = [];
        this.pageIndex = 0;
        this.loadProducts(this.initialPageSize);
    }

    /*** Remove single subcategory from top  ***/
    public removeFilterItem(filterItem: string): void {
        this.selectedSubCategories = this.selectedSubCategories.filter((item) => item !== filterItem);
        this.activeSubCategory = "";
        this.productList = [];
        this.pageIndex = 0;
        this.loadProducts(this.initialPageSize);
    }

    /*** Clear all Subcategories  ***/
    public clearFilterItems(): void {
        this.selectedSubCategories = ["All Products"];
        this.activeSubCategory = "All Products";
        this.selectedDiscount = null;
        this.seletedReviews = [];
        this.productList = [];
        this.pageIndex = 0;
        this.loadProducts(this.initialPageSize);
    }

    public onPriceSortingChange(event: MatSelectChange): void {
        this.selectedSort = event.value;
        this.productList = [];
        this.pageIndex = 0;
        this.loadProducts(this.initialPageSize);
    }

    public getProductQtyInCart(productId: number): number {
        return this.localStorageService.getCartedQuantity(productId);
    }

    public onBlur(event: Event, isMinRange: boolean): void {
        const inputValue = (event.target as HTMLInputElement).value.trim();
        if (!inputValue) {
            (event.target as HTMLInputElement).value = "1";
            if (isMinRange) {
                this.minPrice = parseInt((event.target as HTMLInputElement).value, 10);
            } else {
                this.maxPrice = parseInt((event.target as HTMLInputElement).value, 10);
            }
            this.priceChange$.next({
                min: this.minPrice,
                max: this.maxPrice,
            });
        }
    }

    public onClickProduct(productId: number): void {
        this.router.navigate([`products/${productId}`]);
    }

    public addToCart(product: ProductAttributes, quantity: number) {
        const item: BaseCart = {
            productID: product.id,
            quantity: quantity,
        };
        this.cartService.addToCart(item, product).subscribe((response) => {
            if (response) {
                this.isNavbarOpen = true;
            }
        });
    }

    public onCloseNavbar() {
        this.isNavbarOpen = false;
    }

    public onCustomize(productId: number): void {
        this.router.navigate(["products", productId]);
    }

    public onViewMoreClick(): void {
        this.pageIndex > 1 ? this.pageIndex++ : (this.pageIndex = 3);
        this.loadProducts(this.pageSize);
    }

    private getTranslation(key: string, params?: object): string {
        return this.translate.instant(key, params);
    }

    // public selectSubCategory(subCategory: string): void {
    //     // Avoid duplicate API calls for the active subcategory(ALL)
    //     if (this.activeSubCategory === subCategory && subCategory === "All Products") {
    //         return;
    //     }
    //     // selecting subCategory making pageindex zero
    //     this.pageIndex = 0;
    //     // Case 1: "All Products" is selected
    //     if (subCategory === "All Products") {
    //         this.selectedSubCategories = ["All Products"];
    //         this.activeSubCategory = subCategory;
    //         this.productList = [];
    //         this.loadProducts(this.initialPageSize);
    //         return;
    //     }

    //     // Case 2: Another subcategory is selected
    //     // Remove "All Products" if it was previously selected
    //     const allProductsIndex = this.selectedSubCategories.indexOf("All Products");

    //     if (allProductsIndex !== -1) {
    //         this.selectedSubCategories.splice(allProductsIndex, 1);
    //     }

    //     // Add the selected subcategory if not already selected
    //     if (!this.selectedSubCategories.includes(subCategory)) {
    //         this.selectedSubCategories.push(subCategory);
    //         this.activeSubCategory = subCategory;
    //         this.productList = [];
    //         this.loadProducts(this.initialPageSize);
    //     } else {
    //         // If the subcategory was already selected, remove it
    //         this.selectedSubCategories = this.selectedSubCategories.filter((item) => item !== subCategory);
    //         this.activeSubCategory = "";
    //         this.productList = [];
    //         this.loadProducts(this.initialPageSize);

    //         if(this.selectedSubCategories.length === 0) {
    //             this.selectedSubCategories = ["All Products"];
    //             this.activeSubCategory = "All Products";
    //             this.productList = [];
    //             this.loadProducts(this.initialPageSize);
    //         }
    //     }
    // }

    public selectSubCategory(subCategory: string): void {
        const ALL_PRODUCTS = "All Products";

        // Prevent duplicate API call
        if (this.activeSubCategory === subCategory && subCategory === ALL_PRODUCTS) {
            return;
        }

        this.pageIndex = 0;

        // Handle "All Products"
        if (subCategory === ALL_PRODUCTS) {
            this.setActiveCategory([ALL_PRODUCTS], ALL_PRODUCTS);
            return;
        }

        // Remove "All Products" if another category is selected
        this.selectedSubCategories = this.selectedSubCategories.filter((category) => category !== ALL_PRODUCTS);

        const isSelected = this.selectedSubCategories.includes(subCategory);

        if (isSelected) {
            // Remove selected category
            this.selectedSubCategories = this.selectedSubCategories.filter((category) => category !== subCategory);

            // Fallback to "All Products" if no category remains
            if (this.selectedSubCategories.length === 0) {
                this.setActiveCategory([ALL_PRODUCTS], ALL_PRODUCTS);
            } else {
                this.setActiveCategory(this.selectedSubCategories, "");
            }
        } else {
            // Add new category
            this.selectedSubCategories.push(subCategory);

            this.setActiveCategory(this.selectedSubCategories, subCategory);
        }
    }

    private setActiveCategory(categories: string[], activeCategory: string): void {
        this.selectedSubCategories = categories;
        this.activeSubCategory = activeCategory;
        this.productList = [];
        this.loadProducts(this.initialPageSize);
    }

    public clickgridView(): void {
        this.isListView = false;
    }

    public clickViewList(): void {
        this.isListView = true;
    }

    public toggleFilterButton(): void {
        this.isOpenFilter = !this.isOpenFilter;
    }

    public isActiveSubCategory(subCategory: string): boolean {
        return this.selectedSubCategories.includes(subCategory);
    }

    public setNavbarOpen(isOpen: boolean): void {
        this.isNavbarOpen = isOpen;
    }

    public goBack(): void {
        this.location.back();
    }

    public getAvailableSubCategories(): void {
        this.productService.getAvailableSubCategories(this.minPrice, this.maxPrice, this.category).subscribe({
            next: (response) => {
                this.availableSubCategories = response;
                this.availableSubCategories.unshift("All Products");
            },
            error: (error) => {
                throw error.error;
            },
        });
    }

    ngOnDestroy(): void {
        this.priceChangeSub.unsubscribe();
    }
}
